/* ==========================================================================
   Pool Busters — Fiberglass Design Centre
   PLAN-VIEW GENERATOR  ·  assets/pb-fiberglass-planview.js
   --------------------------------------------------------------------------
   Draws an accurate top-down plan of each shell, generated at render time
   from the published dimensions already in pb-fiberglass-data.js.

   WHY THIS EXISTS
   Until Latham and PENTARM approve their photography, every card needs some
   visual. A single generic graphic repeated across the catalogue would be
   actively misleading — a freeform Aruba and a rectangular Monaco are not the
   same shape, and a customer comparing them deserves to see that.

   What this draws IS true to the manufacturer data:
     · the length-to-width ratio, computed from the published dimensions
     · the shape family, from the published shape descriptor
     · features that are moulded into the shell — tanning ledge, built-in spa,
       bench and swim-up seating, entry steps — from the published feature list

   What it is NOT: a manufacturer engineering drawing. Corner radii, step
   geometry and ledge placement are representative. Every plan is labelled
   accordingly, and the drawer still links to the manufacturer's own
   specification.

   Costs nothing to ship: no images, no requests, ~3 KB of code, drawn as
   inline SVG at whatever size the card needs.
   ========================================================================== */

(function () {
  'use strict';

  var NS = 'http://www.w3.org/2000/svg';

  /* Palette — matches the design system, deliberately not turquoise. */
  var C = {
    paper:  '#f7f3ec',
    water:  '#2f5f76',
    waterHi:'#3d7near',
    shell:  '#0d1c27',
    ledge:  '#6d93a4',
    coping: '#ded7ca',
    ink:    '#4a5761'
  };
  C.waterHi = '#42768f';

  /* ---- dimension parsing ------------------------------------------------
     Handles every format the two manufacturers publish:
       32' x 14'        13'3" x 37'       9'10" x 19'9"
       26′3″ × 13′1″    15'10" x 38'      13'9" x 39'11"
     Returns { len, wid } in decimal feet, longest side first.            */

  function feet(token) {
    var m = token.match(/(\d+(?:\.\d+)?)\s*['′]?\s*(?:(\d+(?:\.\d+)?)\s*["″])?/);
    if (!m) return 0;
    return parseFloat(m[1]) + (m[2] ? parseFloat(m[2]) / 12 : 0);
  }

  function parseDims(dims) {
    if (!dims) return null;
    var parts = String(dims).split(/\s*[x×]\s*/i);
    if (parts.length < 2) return null;
    var a = feet(parts[0]);
    var b = feet(parts[1]);
    if (!a || !b) return null;
    return { len: Math.max(a, b), wid: Math.min(a, b) };
  }

  /* ---- shape families --------------------------------------------------- */

  function family(model) {
    var s = (model.shape || '').toLowerCase();
    if (s.indexOf('kidney') > -1) return 'kidney';
    if (s.indexOf('freeform') > -1 || s.indexOf('free form') > -1) return 'freeform';
    if (s.indexOf('curved') > -1) return 'curved';
    return 'rect';
  }

  /* Build the shell outline as an SVG path, drawn inside the box (x,y,w,h). */
  function outline(kind, x, y, w, h) {
    var r, p;
    if (kind === 'rect') {
      r = Math.min(w, h) * 0.10;
      return 'M' + (x + r) + ',' + y + 'h' + (w - 2 * r) +
             'a' + r + ',' + r + ' 0 0 1 ' + r + ',' + r +
             'v' + (h - 2 * r) +
             'a' + r + ',' + r + ' 0 0 1 ' + (-r) + ',' + r +
             'h' + (-(w - 2 * r)) +
             'a' + r + ',' + r + ' 0 0 1 ' + (-r) + ',' + (-r) +
             'v' + (-(h - 2 * r)) +
             'a' + r + ',' + r + ' 0 0 1 ' + r + ',' + (-r) + 'z';
    }
    if (kind === 'curved') {
      r = h / 2;
      return 'M' + (x + r) + ',' + y + 'h' + (w - 2 * r) +
             'a' + r + ',' + r + ' 0 0 1 0,' + h +
             'h' + (-(w - 2 * r)) +
             'a' + r + ',' + r + ' 0 0 1 0,' + (-h) + 'z';
    }
    if (kind === 'kidney') {
      /* A waisted outline — narrower through the middle on one side. */
      var cy = y + h / 2;
      p  = 'M' + (x + w * 0.06) + ',' + cy;
      p += 'C' + (x + w * 0.02) + ',' + (y + h * 0.10) + ' ' +
                 (x + w * 0.22) + ',' + y + ' ' +
                 (x + w * 0.42) + ',' + (y + h * 0.06);
      p += 'C' + (x + w * 0.58) + ',' + (y + h * 0.12) + ' ' +
                 (x + w * 0.72) + ',' + (y + h * 0.30) + ' ' +
                 (x + w * 0.94) + ',' + (y + h * 0.22);
      p += 'C' + (x + w * 1.02) + ',' + (y + h * 0.55) + ' ' +
                 (x + w * 0.88) + ',' + (y + h * 0.92) + ' ' +
                 (x + w * 0.62) + ',' + (y + h * 0.96);
      p += 'C' + (x + w * 0.38) + ',' + (y + h) + ' ' +
                 (x + w * 0.14) + ',' + (y + h * 0.88) + ' ' +
                 (x + w * 0.06) + ',' + cy + 'z';
      return p;
    }
    /* freeform — a soft, asymmetric organic outline */
    p  = 'M' + (x + w * 0.04) + ',' + (y + h * 0.46);
    p += 'C' + (x + w * 0.04) + ',' + (y + h * 0.14) + ' ' +
               (x + w * 0.20) + ',' + (y + h * 0.02) + ' ' +
               (x + w * 0.44) + ',' + (y + h * 0.04);
    p += 'C' + (x + w * 0.68) + ',' + (y + h * 0.06) + ' ' +
               (x + w * 0.86) + ',' + (y + h * 0.00) + ' ' +
               (x + w * 0.95) + ',' + (y + h * 0.24);
    p += 'C' + (x + w * 1.01) + ',' + (y + h * 0.48) + ' ' +
               (x + w * 0.97) + ',' + (y + h * 0.78) + ' ' +
               (x + w * 0.78) + ',' + (y + h * 0.93);
    p += 'C' + (x + w * 0.58) + ',' + (y + h * 1.02) + ' ' +
               (x + w * 0.24) + ',' + (y + h * 1.00) + ' ' +
               (x + w * 0.10) + ',' + (y + h * 0.80);
    p += 'C' + (x + w * 0.05) + ',' + (y + h * 0.68) + ' ' +
               (x + w * 0.04) + ',' + (y + h * 0.58) + ' ' +
               (x + w * 0.04) + ',' + (y + h * 0.46) + 'z';
    return p;
  }

  function has(model, tag) {
    return (model.tags || []).indexOf(tag) > -1;
  }

  function svgEl(name, attrs) {
    var n = document.createElementNS(NS, name);
    Object.keys(attrs || {}).forEach(function (k) { n.setAttribute(k, attrs[k]); });
    return n;
  }

  /* ---- the drawing ------------------------------------------------------ */

  function build(model) {
    var d = parseDims(model.dims);
    if (!d) return null;

    var VW = 400, VH = 300;          /* viewBox */
    var PAD = 34;
    var maxW = VW - PAD * 2;
    var maxH = VH - PAD * 2 - 12;    /* small gap for the card's own label */

    /* Scale the true ratio to fit, so a 40ft pool reads longer than a 20ft. */
    var ratio = d.len / d.wid;
    var w = maxW, h = maxW / ratio;
    if (h > maxH) { h = maxH; w = maxH * ratio; }

    var x = (VW - w) / 2;
    var y = PAD + (maxH - h) / 2;

    var kind = family(model);
    var path = outline(kind, x, y, w, h);

    var svg = svgEl('svg', {
      viewBox: '0 0 ' + VW + ' ' + VH,
      role: 'img',
      'aria-label': model.brand + ' ' + model.name +
        ' — plan view drawn to the published ratio, ' + model.dims,
      preserveAspectRatio: 'xMidYMid meet'
    });

    var defs = svgEl('defs');
    var grad = svgEl('linearGradient', { id: 'pv-' + model.id, x1: '0', y1: '0', x2: '0.4', y2: '1' });
    grad.appendChild(svgEl('stop', { offset: '0', 'stop-color': C.waterHi }));
    grad.appendChild(svgEl('stop', { offset: '1', 'stop-color': C.water }));
    defs.appendChild(grad);
    svg.appendChild(defs);

    /* coping — a soft surround, reads as decking */
    var coping = svgEl('path', {
      d: path, fill: 'none', stroke: C.coping,
      'stroke-width': 9, 'stroke-linejoin': 'round', opacity: '0.9'
    });
    svg.appendChild(coping);

    /* water */
    svg.appendChild(svgEl('path', {
      d: path, fill: 'url(#pv-' + model.id + ')',
      stroke: C.shell, 'stroke-width': 1.4, 'stroke-linejoin': 'round'
    }));

    var clipId = 'pvc-' + model.id;
    var clip = svgEl('clipPath', { id: clipId });
    clip.appendChild(svgEl('path', { d: path }));
    defs.appendChild(clip);

    var inner = svgEl('g', { 'clip-path': 'url(#' + clipId + ')' });

    /* tanning ledge — a shallow shelf across one end */
    if (has(model, 'tanning-ledge')) {
      inner.appendChild(svgEl('rect', {
        x: x, y: y, width: w * 0.26, height: h,
        fill: C.ledge, opacity: '0.62'
      }));
      inner.appendChild(svgEl('line', {
        x1: x + w * 0.26, y1: y, x2: x + w * 0.26, y2: y + h,
        stroke: C.paper, 'stroke-width': 1.2, opacity: '0.75'
      }));
    }

    /* wading area — a shallower, softer zone */
    if (has(model, 'wading') && !has(model, 'tanning-ledge')) {
      inner.appendChild(svgEl('rect', {
        x: x, y: y, width: w * 0.20, height: h,
        fill: C.ledge, opacity: '0.42'
      }));
    }

    /* bench / swim-up seating — a strip along the far edge */
    if (has(model, 'bench') || has(model, 'swim-up')) {
      inner.appendChild(svgEl('rect', {
        x: x, y: y, width: w, height: h * 0.13,
        fill: C.ledge, opacity: '0.45'
      }));
    }

    /* entry steps — corner treads at the near end */
    inner.appendChild(svgEl('path', {
      d: 'M' + (x + w * 0.02) + ',' + (y + h * 0.72) +
         'h' + (w * 0.16) + 'v' + (h * 0.09) + 'h' + (-w * 0.16) + 'z',
      fill: C.paper, opacity: '0.30'
    }));
    inner.appendChild(svgEl('path', {
      d: 'M' + (x + w * 0.02) + ',' + (y + h * 0.84) +
         'h' + (w * 0.12) + 'v' + (h * 0.09) + 'h' + (-w * 0.12) + 'z',
      fill: C.paper, opacity: '0.22'
    }));

    svg.appendChild(inner);

    /* built-in spa — a separate body, drawn attached to one end */
    if (has(model, 'built-in-spa')) {
      var sr = Math.min(h * 0.36, w * 0.13);
      var sx = x + w - sr * 0.55;
      var sy = y + h * 0.5;
      svg.appendChild(svgEl('circle', {
        cx: sx, cy: sy, r: sr + 4, fill: 'none',
        stroke: C.coping, 'stroke-width': 7, opacity: '0.9'
      }));
      svg.appendChild(svgEl('circle', {
        cx: sx, cy: sy, r: sr, fill: C.waterHi,
        stroke: C.shell, 'stroke-width': 1.4
      }));
    }

    /* porcelain waterline tile — a fine band just inside the shell edge */
    if (has(model, 'porcelain-tile')) {
      svg.appendChild(svgEl('path', {
        d: path, fill: 'none', stroke: C.shell,
        'stroke-width': 3.5, opacity: '0.5',
        'stroke-dasharray': '5 3', 'stroke-linejoin': 'round'
      }));
    }

    /* No dimension caption is drawn here on purpose. The card already lists
       Dimensions and Depth immediately below the image, and the drawer repeats
       them — a caption inside the SVG collided with the card's own
       "Plan view · to published ratio" label. */

    return svg;
  }

  window.PB_FG_PLANVIEW = {
    build: build,
    parseDims: parseDims,
    family: family
  };
})();
